import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card, Form, Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { 
  getVoucherGroup, 
  updateVoucherGroup, 
  getVoucherGroupTypes, 
  getIssueDateTypes,
  getAPAccounts 
} from './api/VoucherGroupApi';

const schema = yup.object().shape({
  groupName: yup.string().required('Group name is required'),
  voucherGroupTypeId: yup.string().required('Group type is required'),
  defaultInitialAmount: yup
    .number()
    .typeError('Must be a number')
    .positive('Must be a positive amount')
    .required('Initial amount is required'),
  defaultExpiresAfterInYears: yup
    .number()
    .typeError('Must be a number')
    .when('doesNotExpire', {
      is: false,
      then: () => yup.number()
        .required('Expiration years is required')
        .positive('Must be a positive number')
        .integer('Must be a whole number'),
      otherwise: () => yup.number().nullable()
    }),
  doesNotExpire: yup.boolean().required(),
  startDate: yup.date().required('Start date is required'),
  endDate: yup.date().transform((value, originalValue) => {
    if (!originalValue || originalValue === '') {
      return null;
    }
    return value;
  }).nullable(),
  anniversaryYearsCriteria: yup
    .number()
    .typeError('Must be a number')
    .when('voucherGroupTypeId', {
      is: 'ANNIVERSARY',
      then: () => yup.number()
        .required('Anniversary years is required for anniversary groups')
        .positive('Must be a positive number')
        .integer('Must be a whole number'),
      otherwise: () => yup.number().nullable()
    }),
  apAccountId: yup.number().required('AP Account is required')
}).required();

const VoucherGroupEditForm = () => {
  const history = useHistory();
  const { voucherGroupId } = useParams();
  const [groupTypes, setGroupTypes] = useState([]);
  const [issueDateTypes, setIssueDateTypes] = useState([]);
  const [apAccounts, setApAccounts] = useState([]);
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      doesNotExpire: false,
      defaultExpiresAfterInYears: null,
      endDate: null
    }
  });

  const selectedType = watch('voucherGroupTypeId');
  const doesNotExpire = watch('doesNotExpire');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all necessary data
        const [group, types, dateTypes, accounts] = await Promise.all([
          getVoucherGroup(voucherGroupId),
          getVoucherGroupTypes(),
          getIssueDateTypes(),
          getAPAccounts()
        ]);

        setGroupTypes(types);
        setIssueDateTypes(dateTypes);
        setApAccounts(accounts);

        // Reset form with existing group data
        reset({
          groupName: group.groupName,
          voucherGroupTypeId: group.voucherGroupTypeId,
          startDate: new Date(group.startDate).toISOString().split('T')[0],
          endDate: group.endDate ? new Date(group.endDate).toISOString().split('T')[0] : null,
          defaultExpiresAfterInYears: group.defaultExpiresAfterInYears,
          doesNotExpire: !group.defaultExpiresAfterInYears,
          defaultInitialAmount: group.defaultInitialAmount,
          anniversaryYearsCriteria: group.anniversaryYearsCriteria,
          apAccountId: group.apAccountId
        });

        setSelectedGroupType(group.voucherGroupTypeId);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
        setLoading(false);
      }
    };

    fetchData();
  }, [voucherGroupId, reset]);

  const handleUpdate = async (data) => {
    try {
      const formattedData = {
        ...data,
        endDate: data.endDate || null
      };
      
      await updateVoucherGroup(voucherGroupId, formattedData);
      history.push(`/travel/vouchergroups/${voucherGroupId}`);
    } catch (error) {
      console.error("Error updating voucher group:", error);
      setError(error);
    }
  };

  const handleCancel = () => {
    history.push(`/travel/vouchergroups/${voucherGroupId}`);
  };

  const handleExpirationChange = (value) => {
    setValue('doesNotExpire', value);
    if (value) {
      setValue('defaultExpiresAfterInYears', null);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <Card className="mx-auto" style={{ maxWidth: '30rem' }}>
      <Card.Body>
        <Card.Title>Edit Allowance Group</Card.Title>
        <Form noValidate onSubmit={handleSubmit(handleUpdate)}>
          <Form.Group className="mb-3">
            <Form.Label>Group Type</Form.Label>
            <Form.Control
              as="select"
              isInvalid={!!errors.voucherGroupTypeId}
              {...register('voucherGroupTypeId')}
            >
              <option value="">Select a group type...</option>
              {groupTypes.map(type => (
                <option key={type.voucherGroupTypeId} value={type.voucherGroupTypeId}>
                  {type.label}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.voucherGroupTypeId?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>AP Account</Form.Label>
            <Form.Control
              as="select"
              isInvalid={!!errors.apAccountId}
              {...register('apAccountId')}
            >
              <option value="">Select an AP account...</option>
              {apAccounts.map(account => (
                <option key={account.apAccountId} value={account.apAccountId}>
                  {account.accountNumber} - {account.description}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.apAccountId?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.groupName}
              {...register('groupName')}
              placeholder="Enter group name"
            />
            <Form.Control.Feedback type="invalid">
              {errors.groupName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              isInvalid={!!errors.startDate}
              {...register('startDate')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.startDate?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>End Date (Optional)</Form.Label>
            <Form.Control
              type="date"
              {...register('endDate')}
            />
          </Form.Group>

          {selectedType === 'ANNIVERSARY' && (
            <Form.Group className="mb-3">
              <Form.Label>Anniversary Years</Form.Label>
              <Form.Control
                type="number"
                isInvalid={!!errors.anniversaryYearsCriteria}
                {...register('anniversaryYearsCriteria')}
                placeholder="Enter years of service required"
              />
              <Form.Control.Feedback type="invalid">
                {errors.anniversaryYearsCriteria?.message}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Default Expires After</Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                type="radio"
                label="Expires after"
                id="expires-after"
                checked={!doesNotExpire}
                onChange={() => handleExpirationChange(false)}
              />
              <Form.Control
                type="number"
                placeholder="Years"
                style={{ width: '100px' }}
                disabled={doesNotExpire}
                isInvalid={!!errors.defaultExpiresAfterInYears}
                {...register('defaultExpiresAfterInYears')}
              />
              <Form.Check
                type="radio"
                label="Does not expire"
                id="does-not-expire"
                checked={doesNotExpire}
                onChange={() => handleExpirationChange(true)}
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.defaultExpiresAfterInYears?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Default Initial Amount</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              isInvalid={!!errors.defaultInitialAmount}
              {...register('defaultInitialAmount')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.defaultInitialAmount?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button variant="primary" type="submit">
              Update
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default VoucherGroupEditForm;